import {
  Alert,
  Button,
  Empty,
  Input,
  InputNumber,
  Progress,
  Select,
  Space,
  Table,
  Tabs,
  Tag,
  Typography,
  message,
} from "antd";
import { ClearOutlined, DownloadOutlined, ReloadOutlined, WarningOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { call } from "../tsrpc";
import CommercialAnalyticsPanel from "./analytics/CommercialAnalyticsPanel";
import {
  AnalyticsTone,
  ConversionGrid,
  ConversionLabel,
  ConversionMetric,
  ConversionValue,
  DashboardStack,
  FilterPanel,
  FunnelLine,
  FunnelOutcome,
  FunnelRate,
  FunnelReach,
  FunnelVisual,
  Header,
  HeaderActions,
  HeaderCopy,
  InsightGrid,
  KpiCard,
  KpiGrid,
  KpiLabel,
  KpiValue,
  MetricHint,
  MiniBar,
  MiniBarFill,
  Muted,
  OverviewBottomGrid,
  OverviewTopGrid,
  PageWrap,
  Panel,
  PanelHeader,
  PanelTitle,
  RiskLine,
  StepIndex,
  StepName,
  StickyControls,
  TableSurface,
  TabsSurface,
  TrendBars,
  TrendFill,
  TrendLegend,
  TrendLegendItem,
  TrendLine,
  TrendTrack,
} from "./analytics/experienceAnalyticsStyles";

const { Text, Title } = Typography;

type AnalyticsKind = "overview" | "funnel" | "taskStats" | "propStats" | "roundStats" | "effectStats" | "choiceStats" | "retention" | "sessions";
type FunnelPresetKey = "main" | "task" | "alchemy" | "custom";

type FilterState = {
  rangeDays: number;
  grade?: number;
  level?: number;
  taskIndex?: number;
  potionId?: string;
  propId?: string;
  platform?: string;
  appVersion?: string;
  resourceVersion?: string;
  funnelPreset: FunnelPresetKey;
  customSteps: string[];
};

const DEFAULT_FILTERS: FilterState = {
  rangeDays: 7,
  funnelPreset: "main",
  customSteps: ["app_start", "login_success", "level_enter", "task_exposed", "task_start", "recipe_select_show", "recipe_selected", "round_end", "task_success", "delivery_confirm"],
};

const FUNNEL_PRESETS: Record<Exclude<FunnelPresetKey, "custom">, { label: string; steps: string[] }> = {
  main: {
    label: "主流程",
    steps: ["app_start", "login_success", "level_enter", "task_exposed", "task_start", "recipe_select_show", "recipe_selected", "round_end", "task_success", "delivery_confirm"],
  },
  task: {
    label: "任务启动",
    steps: ["level_enter", "task_exposed", "task_start", "recipe_select_show", "recipe_selected", "round_end"],
  },
  alchemy: {
    label: "炼金完成",
    steps: ["recipe_select_show", "recipe_selected", "round_end", "task_success", "delivery_confirm"],
  },
};

const EVENT_LABELS: Record<string, string> = {
  app_start: "应用启动",
  login_success: "登录成功",
  level_enter: "进入关卡",
  level_exit: "离开关卡",
  task_exposed: "任务曝光",
  task_start: "任务开始",
  task_success: "任务成功",
  task_fail: "任务失败",
  delivery_confirm: "确认交付",
  recipe_select_show: "配方选择展示",
  recipe_selected: "配方选择完成",
  prop_selected: "道具被选中",
  round_start: "回合开始",
  round_end: "回合结束",
  commission_end: "委托结束",
  prop_acquired: "获得道具",
  prop_used: "使用道具",
  prop_magic: "道具魔力变化",
  prop_transformed: "道具异变",
  round_summary: "回合质量快照",
  prop_effect_summary: "机制效果汇总",
  __end: "无后续事件",
};


const EFFECT_LABELS: Record<string, string> = {
  grow: "成长",
  extraYield: "额外产出",
  protect: "保护",
  purify: "净化",
  accelerate: "加速",
  eliminate: "消除",
  transform: "转化",
  spawn: "生成",
  collect: "收集",
  harvest: "收获",
  rarityWeight: "品质权重",
  extendCommission: "延长委托",
  swapPosition: "交换位置",
  permanentBoost: "永久加成",
};

const EVENT_OPTIONS = Object.entries(EVENT_LABELS)
  .filter(([value]) => value !== "__end")
  .map(([value, label]) => ({ value, label }));

export default function PageAnalytics() {
  const [filters, setFilters] = useState<FilterState>(DEFAULT_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState<FilterState>(DEFAULT_FILTERS);
  const [activeTab, setActiveTab] = useState("commercial");
  const [loading, setLoading] = useState(false);
  const [loadingKinds, setLoadingKinds] = useState<Partial<Record<AnalyticsKind, boolean>>>({});
  const [overview, setOverview] = useState<any>();
  const [funnel, setFunnel] = useState<any>();
  const [taskStats, setTaskStats] = useState<any[]>([]);
  const [propStats, setPropStats] = useState<any[]>([]);
  const [roundStats, setRoundStats] = useState<any[]>([]);
  const [effectStats, setEffectStats] = useState<any[]>([]);
  const [choiceStats, setChoiceStats] = useState<any[]>([]);
  const [retention, setRetention] = useState<any>();
  const [sessions, setSessions] = useState<any[]>([]);
  const requestVersionRef = useRef(0);
  const sessionsLoadedVersionRef = useRef(0);

  useEffect(() => {
    const timer = window.setTimeout(() => setAppliedFilters(filters), 400);
    return () => window.clearTimeout(timer);
  }, [filters]);

  const appliedSteps = useMemo(() => {
    if (appliedFilters.funnelPreset === "custom") return appliedFilters.customSteps.filter(Boolean);
    return FUNNEL_PRESETS[appliedFilters.funnelPreset].steps;
  }, [appliedFilters.funnelPreset, appliedFilters.customSteps]);

  const buildQueryParams = useCallback(() => {
    const endTime = Date.now();
    const startTime = endTime - appliedFilters.rangeDays * 24 * 60 * 60 * 1000;
    return compactObject({
      startTime,
      endTime,
      grade: appliedFilters.grade,
      level: appliedFilters.level,
      taskIndex: appliedFilters.taskIndex,
      potionId: appliedFilters.potionId?.trim(),
      propId: appliedFilters.propId?.trim(),
      platform: appliedFilters.platform?.trim(),
      appVersion: appliedFilters.appVersion?.trim(),
      resourceVersion: appliedFilters.resourceVersion?.trim(),
      steps: appliedSteps.length ? appliedSteps : DEFAULT_FILTERS.customSteps,
      limit: 100,
    });
  }, [appliedFilters, appliedSteps]);

  const applyResult = useCallback((kind: AnalyticsKind, data: any) => {
    if (kind === "overview") setOverview(data);
    else if (kind === "funnel") setFunnel(data);
    else if (kind === "taskStats") setTaskStats(Array.isArray(data) ? data : []);
    else if (kind === "propStats") setPropStats(Array.isArray(data) ? data : []);
    else if (kind === "roundStats") setRoundStats(Array.isArray(data) ? data : []);
    else if (kind === "effectStats") setEffectStats(Array.isArray(data) ? data : []);
    else if (kind === "choiceStats") setChoiceStats(Array.isArray(data) ? data : []);
    else if (kind === "retention") setRetention(data);
    else if (kind === "sessions") setSessions(Array.isArray(data) ? data : []);
  }, []);

  const loadKind = useCallback(async (
    kind: AnalyticsKind,
    queryBase: Record<string, any>,
    requestVersion: number,
  ) => {
    setLoadingKinds((current) => ({ ...current, [kind]: true }));
    try {
      const data = await queryAnalytics(kind, queryBase);
      if (requestVersionRef.current === requestVersion && data !== undefined) {
        applyResult(kind, data);
      }
    } catch (error: any) {
      if (requestVersionRef.current === requestVersion) {
        message.error(error?.message || "数据统计查询失败");
      }
    } finally {
      if (requestVersionRef.current === requestVersion) {
        setLoadingKinds((current) => ({ ...current, [kind]: false }));
      }
    }
  }, [applyResult]);

  const load = useCallback(async () => {
    const queryBase = buildQueryParams();
    const requestVersion = requestVersionRef.current + 1;
    requestVersionRef.current = requestVersion;
    sessionsLoadedVersionRef.current = 0;
    setSessions([]);
    setLoading(true);

    const batches: AnalyticsKind[][] = [
      ["overview"],
      ["funnel", "retention"],
      ["taskStats", "propStats"],
      ["roundStats", "choiceStats"],
      ["effectStats"],
    ];
    try {
      for (const batch of batches) {
        if (requestVersionRef.current !== requestVersion) break;
        await Promise.allSettled(batch.map((kind) => loadKind(kind, queryBase, requestVersion)));
      }
    } finally {
      if (requestVersionRef.current === requestVersion) setLoading(false);
    }
  }, [buildQueryParams, loadKind]);

  const loadSessions = useCallback(async () => {
    const requestVersion = requestVersionRef.current;
    if (!requestVersion || sessionsLoadedVersionRef.current === requestVersion) return;
    await loadKind("sessions", buildQueryParams(), requestVersion);
    if (requestVersionRef.current === requestVersion) sessionsLoadedVersionRef.current = requestVersion;
  }, [buildQueryParams, loadKind]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (activeTab === "sessions") loadSessions();
  }, [activeTab, loadSessions]);

  const kpis = overview?.kpis || {};
  const funnelSteps = Array.isArray(funnel?.steps) ? funnel.steps : [];
  const presetName = filters.funnelPreset === "custom" ? "自定义" : FUNNEL_PRESETS[filters.funnelPreset].label;

  return (
    <PageWrap>
      <StickyControls>
        <Header>
          <HeaderCopy>
            <Title level={4} style={{ margin: 0 }}>数据统计</Title>
            <Text type="secondary">
              {activeTab === "commercial"
                ? "激活归因与广告播放质量"
                : formatRange(overview?.range || funnel?.range)}
            </Text>
          </HeaderCopy>
          {activeTab !== "commercial" && (
            <HeaderActions>
              <Button icon={<DownloadOutlined />} onClick={() => exportAnalyticsCsv({ overview, funnel, taskStats, propStats, roundStats, effectStats, choiceStats, retention, sessions })}>导出 CSV</Button>
              <Button icon={<ClearOutlined />} onClick={() => setFilters(DEFAULT_FILTERS)}>清空筛选</Button>
              <Button
                type="primary"
                icon={<ReloadOutlined />}
                loading={loading}
                onClick={() => appliedFilters === filters ? load() : setAppliedFilters(filters)}
              >刷新</Button>
            </HeaderActions>
          )}
        </Header>

        {activeTab !== "commercial" && <FilterPanel>
          <Select
            value={filters.rangeDays}
            style={{ width: 120 }}
            options={[{ label: "最近1天", value: 1 }, { label: "最近7天", value: 7 }, { label: "最近30天", value: 30 }]}
            onChange={(rangeDays) => setFilters((prev) => ({ ...prev, rangeDays }))}
          />
          <Select
            value={filters.funnelPreset}
            style={{ width: 120 }}
            options={[
              ...Object.entries(FUNNEL_PRESETS).map(([value, preset]) => ({ value, label: preset.label })),
              { value: "custom", label: "自定义" },
            ]}
            onChange={(funnelPreset) => setFilters((prev) => ({ ...prev, funnelPreset: funnelPreset as FunnelPresetKey }))}
          />
          {filters.funnelPreset === "custom" && (
            <Select
              mode="tags"
              value={filters.customSteps}
              options={EVENT_OPTIONS}
              style={{ minWidth: 360 }}
              placeholder="选择或输入漏斗事件"
              onChange={(customSteps) => setFilters((prev) => ({ ...prev, customSteps }))}
            />
          )}
          <InputNumber placeholder="段位" value={filters.grade} min={0} style={{ width: 100 }} onChange={(grade) => setFilters((prev) => ({ ...prev, grade: readOptionalNumber(grade) }))} />
          <InputNumber placeholder="关卡" value={filters.level} min={0} style={{ width: 100 }} onChange={(level) => setFilters((prev) => ({ ...prev, level: readOptionalNumber(level) }))} />
          <InputNumber placeholder="任务序号" value={filters.taskIndex} min={0} style={{ width: 110 }} onChange={(taskIndex) => setFilters((prev) => ({ ...prev, taskIndex: readOptionalNumber(taskIndex) }))} />
          <Input placeholder="随机药水编号" allowClear value={filters.potionId} style={{ width: 150 }} onChange={(event) => setFilters((prev) => ({ ...prev, potionId: event.target.value }))} />
          <Input placeholder="道具编号" allowClear value={filters.propId} style={{ width: 160 }} onChange={(event) => setFilters((prev) => ({ ...prev, propId: event.target.value }))} />
          <Input placeholder="平台" allowClear value={filters.platform} style={{ width: 130 }} onChange={(event) => setFilters((prev) => ({ ...prev, platform: event.target.value }))} />
          <Input placeholder="应用版本" allowClear value={filters.appVersion} style={{ width: 130 }} onChange={(event) => setFilters((prev) => ({ ...prev, appVersion: event.target.value }))} />
          <Input placeholder="资源版本" allowClear value={filters.resourceVersion} style={{ width: 130 }} onChange={(event) => setFilters((prev) => ({ ...prev, resourceVersion: event.target.value }))} />
        </FilterPanel>}
      </StickyControls>

      {activeTab !== "commercial" && <KpiGrid>
        <Kpi tone="blue" title="活跃玩家" value={kpis.activeUsers} hint="去重玩家数" />
        <Kpi tone="cyan" title="进入关卡" value={kpis.levelEnters} hint={`启动后进入率 ${percent(rate(kpis.levelEnters, kpis.appStarts))}`} />
        <Kpi tone="gold" title="任务开始" value={kpis.taskStarts} hint={`关卡内开始率 ${percent(rate(kpis.taskStarts, kpis.levelEnters))}`} />
        <Kpi tone="green" title="任务成功" value={kpis.taskSuccesses} hint={`任务完成率 ${percent(rate(kpis.taskSuccesses, kpis.taskStarts))}`} />
        <Kpi tone="red" title="任务失败" value={kpis.taskFails} hint={`任务失败率 ${percent(rate(kpis.taskFails, kpis.taskStarts))}`} />
        <Kpi tone="purple" title="确认交付" value={kpis.deliveryConfirms} hint={`成功后交付率 ${percent(rate(kpis.deliveryConfirms, kpis.taskSuccesses))}`} />
      </KpiGrid>}

      <TabsSurface>
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={[
            {
              key: "commercial",
              label: "商业化统计",
              children: <CommercialAnalyticsPanel />,
            },
            {
              key: "dashboard",
              label: "体验总览",
              children: <Dashboard loading={Boolean(loadingKinds.overview || loadingKinds.funnel)} kpis={kpis} funnel={funnel} funnelSteps={funnelSteps} taskStats={taskStats} propStats={propStats} roundStats={roundStats} effectStats={effectStats} choiceStats={choiceStats} retention={retention} trends={overview?.trends || []} platforms={overview?.platforms || []} presetName={presetName} />,
            },
            { key: "funnel", label: "流程流失", children: <TableSurface><FunnelTable data={funnelSteps} loading={Boolean(loadingKinds.funnel)} /></TableSurface> },
            { key: "retention", label: "留存分析", children: <TableSurface><RetentionTable data={retention?.rows || []} loading={Boolean(loadingKinds.retention)} /></TableSurface> },
            { key: "tasks", label: "委托统计", children: <TableSurface><TaskStatsTable data={taskStats} loading={Boolean(loadingKinds.taskStats)} /></TableSurface> },
            { key: "rounds", label: "回合分析", children: <TableSurface><RoundStatsTable data={roundStats} loading={Boolean(loadingKinds.roundStats)} /></TableSurface> },
            { key: "effects", label: "机制贡献", children: <TableSurface><EffectStatsTable data={effectStats} loading={Boolean(loadingKinds.effectStats)} /></TableSurface> },
            { key: "choices", label: "三选一质量", children: <TableSurface><ChoiceStatsTable data={choiceStats} loading={Boolean(loadingKinds.choiceStats)} /></TableSurface> },
            { key: "props", label: "道具统计", children: <TableSurface><PropStatsTable data={propStats} loading={Boolean(loadingKinds.propStats)} /></TableSurface> },
            { key: "events", label: "事件统计", children: <TableSurface><EventTable data={overview?.events || []} loading={Boolean(loadingKinds.overview)} /></TableSurface> },
            { key: "sessions", label: "最近运行", children: <TableSurface><SessionTable data={sessions} loading={Boolean(loadingKinds.sessions)} /></TableSurface> },
          ]}
        />
      </TabsSurface>
    </PageWrap>
  );
}

function Dashboard({
  loading,
  kpis,
  funnel,
  funnelSteps,
  taskStats,
  propStats,
  roundStats,
  effectStats,
  choiceStats,
  retention,
  trends,
  platforms,
  presetName,
}: {
  loading: boolean;
  kpis: any;
  funnel: any;
  funnelSteps: any[];
  taskStats: any[];
  propStats: any[];
  roundStats: any[];
  effectStats: any[];
  choiceStats: any[];
  retention: any;
  trends: any[];
  platforms: any[];
  presetName: string;
}) {
  const worstStep = findWorstFunnelStep(funnelSteps);
  const completionRate = rate(funnel?.completedRunCount, funnel?.runCount);
  const riskyTasks = useMemo(() => buildRiskyTasks(taskStats), [taskStats]);
  const propRisks = useMemo(() => buildPropRisks(propStats), [propStats]);
  const roundRisks = useMemo(() => buildRoundRisks(roundStats), [roundStats]);
  const effectHighlights = useMemo(() => buildEffectHighlights(effectStats), [effectStats]);
  const choiceRisks = useMemo(() => buildChoiceRisks(choiceStats), [choiceStats]);
  const trendRows = useMemo(() => buildTrendRows(trends), [trends]);

  return (
    <DashboardStack>
      {worstStep && Number(worstStep.dropRate || 0) >= 0.3 && (
        <Alert
          showIcon
          type="warning"
          icon={<WarningOutlined />}
          message={`当前最大流失点：${eventText(worstStep.step)}，流失率 ${percent(worstStep.dropRate)}，流失 ${integerText(worstStep.dropped)} 次`}
        />
      )}
      <OverviewTopGrid>
        <Panel>
          <PanelHeader>
            <PanelTitle>{presetName}漏斗</PanelTitle>
            <Tag color={completionRate >= 0.6 ? "green" : completionRate >= 0.35 ? "gold" : "red"}>总完成率 {percent(completionRate)}</Tag>
          </PanelHeader>
          <FunnelSummary steps={funnelSteps} loading={loading} />
        </Panel>
        <Panel>
          <PanelHeader>
            <PanelTitle>转化诊断</PanelTitle>
            <Tag>运行数 {integerText(funnel?.runCount)}</Tag>
          </PanelHeader>
          <ConversionGrid>
            <MiniMetric title="启动到进关" value={rate(kpis.levelEnters, kpis.appStarts)} />
            <MiniMetric title="进关到开始" value={rate(kpis.taskStarts, kpis.levelEnters)} />
            <MiniMetric title="开始到成功" value={rate(kpis.taskSuccesses, kpis.taskStarts)} />
            <MiniMetric title="成功到交付" value={rate(kpis.deliveryConfirms, kpis.taskSuccesses)} />
          </ConversionGrid>
        </Panel>
      </OverviewTopGrid>

      <InsightGrid>
        <Panel>
          <PanelHeader>
            <PanelTitle>留存概览</PanelTitle>
            <Tag color="cyan">进入关卡 Cohort</Tag>
          </PanelHeader>
          <RetentionSummary rows={retention?.rows || []} />
        </Panel>
        <Panel>
          <PanelHeader>
            <PanelTitle>高风险任务</PanelTitle>
            <Tag color="red">失败与退出</Tag>
          </PanelHeader>
          <RiskList rows={riskyTasks} />
        </Panel>
        <Panel>
          <PanelHeader>
            <PanelTitle>道具转化</PanelTitle>
            <Tag color="blue">选择未使用</Tag>
          </PanelHeader>
          <RiskList rows={propRisks} />
        </Panel>
        <Panel>
          <PanelHeader>
            <PanelTitle>低产回合</PanelTitle>
            <Tag color="gold">平均产出</Tag>
          </PanelHeader>
          <RiskList rows={roundRisks} />
        </Panel>
        <Panel>
          <PanelHeader>
            <PanelTitle>机制高频触发</PanelTitle>
            <Tag color="purple">效果次数</Tag>
          </PanelHeader>
          <RiskList rows={effectHighlights} />
        </Panel>
        <Panel>
          <PanelHeader>
            <PanelTitle>低选择候选</PanelTitle>
            <Tag color="orange">三选一选择率</Tag>
          </PanelHeader>
          <RiskList rows={choiceRisks} />
        </Panel>
      </InsightGrid>

      <OverviewBottomGrid>
        <Panel>
          <PanelHeader>
            <PanelTitle>每日趋势</PanelTitle>
            <Tag>最近 {trendRows.length} 天</Tag>
          </PanelHeader>
          <TrendList rows={trendRows} />
        </Panel>
        <Panel>
          <PanelHeader>
            <PanelTitle>平台与版本</PanelTitle>
            <Tag>前20项</Tag>
          </PanelHeader>
          <PlatformTable data={platforms} loading={loading} />
        </Panel>
      </OverviewBottomGrid>
    </DashboardStack>
  );
}

function Kpi({ title, value, hint, tone }: { title: string; value?: number; hint?: string; tone?: AnalyticsTone }) {
  return (
    <KpiCard $tone={tone}>
      <KpiLabel>{title}</KpiLabel>
      <KpiValue>{integerText(value)}</KpiValue>
      {hint && <MetricHint>{hint}</MetricHint>}
    </KpiCard>
  );
}

function MiniMetric({ title, value }: { title: string; value: number }) {
  const valuePercent = percentNumber(value);
  return (
    <ConversionMetric>
      <ConversionLabel>{title}</ConversionLabel>
      <ConversionValue>
        <span>{valuePercent.toFixed(1)}%</span>
        <Tag color={riskColor(1 - value)}>{value >= 0.6 ? "健康" : value >= 0.35 ? "关注" : "偏低"}</Tag>
      </ConversionValue>
      <Progress percent={valuePercent} size="small" showInfo={false} strokeColor={progressColor(value)} />
    </ConversionMetric>
  );
}

function FunnelSummary({ steps, loading }: { steps: any[]; loading: boolean }) {
  if (!steps.length && !loading) return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无漏斗数据" />;
  const firstReached = Math.max(1, Number(steps[0]?.reached || 0));
  return (
    <div>
      {steps.map((step, index) => {
        const conversion = rate(step.next, step.reached);
        const drop = rate(step.dropped, step.reached);
        const reachPercent = percentNumber(rate(step.reached, firstReached));
        const tone = conversion < 0.35 ? "danger" : conversion < 0.6 ? "warn" : "ok";
        return (
          <FunnelLine key={`${step.step}-${index}`}>
            <StepIndex>{index + 1}</StepIndex>
            <div>
              <StepName>{eventText(step.step)}</StepName>
              <Muted>到达 {integerText(step.reached)}，进入下一步 {integerText(step.next)}</Muted>
            </div>
            <FunnelVisual title={`相对首步到达率 ${reachPercent.toFixed(1)}%`}>
              <FunnelReach $percent={reachPercent} $tone={tone} />
            </FunnelVisual>
            <FunnelOutcome>
              <FunnelRate>
                {percent(conversion)}
                <Tag color={riskColor(drop)}>流失 {percent(drop)}</Tag>
              </FunnelRate>
              <Muted>耗时 {durationText(step.averageTimeToNext)}</Muted>
            </FunnelOutcome>
          </FunnelLine>
        );
      })}
    </div>
  );
}

function RetentionSummary({ rows }: { rows: any[] }) {
  if (!rows.length) return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无留存数据" />;
  const latest = rows[rows.length - 1];
  return (
    <ConversionGrid>
      <MiniMetric title="D1 留存" value={Number(latest.d1Rate || 0)} />
      <MiniMetric title="D3 留存" value={Number(latest.d3Rate || 0)} />
      <MiniMetric title="D7 留存" value={Number(latest.d7Rate || 0)} />
      <ConversionMetric>
        <ConversionLabel>最新 Cohort</ConversionLabel>
        <ConversionValue>{latest.day}</ConversionValue>
        <Muted>{integerText(latest.users)} 名玩家</Muted>
      </ConversionMetric>
    </ConversionGrid>
  );
}

function RiskList({ rows }: { rows: any[] }) {
  if (!rows.length) return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无数据" />;
  return (
    <div>
      {rows.slice(0, 8).map((row, index) => (
        <RiskLine key={`${index}-${row.key}`}>
          <div>
            <Text strong>{row.name}</Text>
            <Muted>{row.detail}</Muted>
          </div>
          <div>
            <MiniBar>
              <MiniBarFill $percent={percentNumber(row.rate)} $tone={row.rate >= 0.4 ? "danger" : row.rate >= 0.2 ? "warn" : "ok"} />
            </MiniBar>
            <Muted>{row.rateLabel}</Muted>
          </div>
          <Tag color={riskColor(row.rate)}>{row.countLabel}</Tag>
        </RiskLine>
      ))}
    </div>
  );
}

function TrendList({ rows }: { rows: any[] }) {
  if (!rows.length) return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无趋势数据" />;
  const maximum = Math.max(
    1,
    ...rows.flatMap((row) => [row.levelEnter, row.taskStart, row.taskSuccess, row.taskFail].map(Number)),
  );
  const series = [
    { key: "levelEnter", label: "进入", color: "#2563eb" },
    { key: "taskStart", label: "开始", color: "#d97706" },
    { key: "taskSuccess", label: "成功", color: "#16a34a" },
    { key: "taskFail", label: "失败", color: "#dc2626" },
  ];
  return (
    <div>
      <TrendLegend>
        {series.map((item) => <TrendLegendItem key={item.key} $color={item.color}>{item.label}</TrendLegendItem>)}
      </TrendLegend>
      <TrendLine>
        <Text type="secondary">日期</Text>
        <Text type="secondary">趋势</Text>
        <Text type="secondary">进入</Text>
        <Text type="secondary">开始</Text>
        <Text type="secondary">成功</Text>
        <Text type="secondary">失败</Text>
      </TrendLine>
      {rows.map((row) => (
        <TrendLine key={row.day}>
          <Text>{row.day}</Text>
          <TrendBars title={series.map((item) => `${item.label} ${integerText(row[item.key])}`).join("；")}>
            {series.map((item) => (
              <TrendTrack key={item.key}>
                <TrendFill $percent={(Number(row[item.key] || 0) / maximum) * 100} $color={item.color} />
              </TrendTrack>
            ))}
          </TrendBars>
          <Text>{integerText(row.levelEnter)}</Text>
          <Text>{integerText(row.taskStart)}</Text>
          <Text>{integerText(row.taskSuccess)}</Text>
          <Text type={row.taskFail > row.taskSuccess ? "danger" : undefined}>{integerText(row.taskFail)}</Text>
        </TrendLine>
      ))}
    </div>
  );
}

function FunnelTable({ data, loading }: { data: any[]; loading: boolean }) {
  const firstReached = Number(data[0]?.reached || 0);
  return (
    <Table
      rowKey={(row) => row.step}
      size="small"
      loading={loading}
      dataSource={data}
      pagination={false}
      scroll={{ x: 1150 }}
      columns={[
        { title: "步骤", dataIndex: "step", width: 170, render: renderEventName },
        { title: "到达数", dataIndex: "reached", width: 110 },
        { title: "进入下一步", dataIndex: "next", width: 110 },
        { title: "流失数", dataIndex: "dropped", width: 110 },
        { title: "流失率", dataIndex: "dropRate", width: 110, render: renderRiskRate },
        { title: "转化率", dataIndex: "conversionRate", width: 140, render: renderProgressRate },
        { title: "首步转化", width: 110, render: (_: any, row: any) => percent(rate(row.reached, firstReached)) },
        { title: "平均耗时", dataIndex: "averageTimeToNext", width: 110, render: durationText },
        { title: "流失后行为", dataIndex: "dropNextEvents", render: renderDropNextEvents },
        { title: "下一步", dataIndex: "nextStep", width: 150, render: renderEventName },
      ]}
    />
  );
}

function RetentionTable({ data, loading }: { data: any[]; loading: boolean }) {
  return (
    <Table
      rowKey="day"
      size="small"
      loading={loading}
      dataSource={data}
      pagination={false}
      columns={[
        { title: "Cohort 日期", dataIndex: "day", width: 140 },
        { title: "进入关卡玩家", dataIndex: "users", width: 130 },
        { title: "D1 留存", width: 150, render: (_: any, row: any) => renderRetentionCell(row.d1, row.d1Rate) },
        { title: "D3 留存", width: 150, render: (_: any, row: any) => renderRetentionCell(row.d3, row.d3Rate) },
        { title: "D7 留存", width: 150, render: (_: any, row: any) => renderRetentionCell(row.d7, row.d7Rate) },
      ]}
    />
  );
}

function TaskStatsTable({ data, loading }: { data: any[]; loading: boolean }) {
  return (
    <Table
      rowKey={(row) => [row.grade, row.level, row.taskIndex, row.taskGroupId, row.taskId].join(":")}
      size="small"
      loading={loading}
      dataSource={data}
      scroll={{ x: 1350 }}
      columns={[
        { title: "段位", dataIndex: "grade", width: 80 },
        { title: "关卡", dataIndex: "level", width: 80 },
        { title: "任务序号", dataIndex: "taskIndex", width: 90 },
        { title: "任务组编号", dataIndex: "taskGroupId", width: 140 },
        { title: "任务编号", dataIndex: "taskId", width: 140 },
        { title: "运行数", dataIndex: "runCount", width: 90 },
        { title: "委托数", dataIndex: "commissionCount", width: 90 },
        { title: "随机药水种类", dataIndex: "potionIdCount", width: 120 },
        { title: "曝光数", dataIndex: "exposed", width: 90 },
        { title: "开始数", dataIndex: "started", width: 90 },
        { title: "成功数", dataIndex: "success", width: 90 },
        { title: "失败数", dataIndex: "fail", width: 90 },
        { title: "退出数", dataIndex: "exit", width: 90 },
        { title: "完成率", dataIndex: "completionRate", width: 120, render: renderProgressRate },
        { title: "失败率", dataIndex: "failRate", width: 110, render: renderRiskRate },
        { title: "退出率", dataIndex: "exitRate", width: 110, render: renderRiskRate },
        { title: "平均得分", dataIndex: "averageScore", width: 110, render: decimalText },
        { title: "目标均分", dataIndex: "averageTargetScore", width: 110, render: decimalText },
        { title: "平均回合", dataIndex: "averageRounds", width: 110, render: decimalText },
      ]}
    />
  );
}

function PropStatsTable({ data, loading }: { data: any[]; loading: boolean }) {
  return (
    <Table
      rowKey={(row) => row.prototypeId || row.assetId || row.name || "unknown"}
      size="small"
      loading={loading}
      dataSource={data}
      scroll={{ x: 1120 }}
      columns={[
        { title: "道具名称", dataIndex: "name", width: 180 },
        { title: "道具原型编号", dataIndex: "prototypeId", width: 220 },
        { title: "资源编号", dataIndex: "assetId", width: 140 },
        { title: "类型", dataIndex: "type", width: 80 },
        { title: "品质", dataIndex: "rarity", width: 80 },
        { title: "玩家数", dataIndex: "userCount", width: 90 },
        { title: "获得数", dataIndex: "acquired", width: 90 },
        { title: "选择数", dataIndex: "selected", width: 90 },
        { title: "使用数", dataIndex: "used", width: 90 },
        { title: "使用率", dataIndex: "useRate", width: 120, render: renderProgressRate },
        { title: "魔力事件", dataIndex: "magicEvents", width: 100 },
        { title: "魔力总和", dataIndex: "magicSum", width: 110, render: decimalText },
        { title: "平均魔力", dataIndex: "averageMagic", width: 110, render: decimalText },
      ]}
    />
  );
}


function RoundStatsTable({ data, loading }: { data: any[]; loading: boolean }) {
  return (
    <Table
      rowKey={(row) => [row.grade, row.level, row.taskIndex, row.commissionIndex, row.roundIndex].join(":")}
      size="small"
      loading={loading}
      dataSource={data}
      scroll={{ x: 1380 }}
      columns={[
        { title: "段位", dataIndex: "grade", width: 80 },
        { title: "关卡", dataIndex: "level", width: 80 },
        { title: "任务序号", dataIndex: "taskIndex", width: 90 },
        { title: "委托序号", dataIndex: "commissionIndex", width: 90 },
        { title: "回合", dataIndex: "roundIndex", width: 80 },
        { title: "样本", dataIndex: "roundCount", width: 80 },
        { title: "玩家数", dataIndex: "userCount", width: 90 },
        { title: "运行数", dataIndex: "runCount", width: 90 },
        { title: "平均本轮魔力", dataIndex: "averageRoundMagic", width: 130, render: decimalText },
        { title: "累计魔力", dataIndex: "averageTotalMagic", width: 110, render: decimalText },
        { title: "最低/最高", width: 120, render: (_: any, row: any) => decimalText(row.minRoundMagic) + " / " + decimalText(row.maxRoundMagic) },
        { title: "盘面道具", dataIndex: "averageBoardItemCount", width: 100, render: decimalText },
        { title: "空格", dataIndex: "averageBoardEmptyCount", width: 90, render: decimalText },
        { title: "材料", dataIndex: "averageMaterialCount", width: 90, render: decimalText },
        { title: "配方", dataIndex: "averageRecipeCount", width: 90, render: decimalText },
        { title: "辅料", dataIndex: "averageConsumableCount", width: 90, render: decimalText },
        { title: "永久加成", dataIndex: "averagePermanentBoostRuleCount", width: 100, render: decimalText },
        { title: "品质权重", dataIndex: "averageRarityWeightRuleCount", width: 100, render: decimalText },
        { title: "本轮加成", dataIndex: "averageRoundYieldBoostCount", width: 100, render: decimalText },
      ]}
    />
  );
}

function EffectStatsTable({ data, loading }: { data: any[]; loading: boolean }) {
  return (
    <Table
      rowKey={(row) => [row.sourcePrototypeId, row.effectKind].join(":")}
      size="small"
      loading={loading}
      dataSource={data}
      scroll={{ x: 1380 }}
      columns={[
        { title: "来源道具", dataIndex: "sourceName", width: 180, render: emptyText },
        { title: "来源原型", dataIndex: "sourcePrototypeId", width: 220, render: emptyText },
        { title: "效果类型", dataIndex: "effectKind", width: 130, render: renderEffectName },
        { title: "品质", dataIndex: "sourceRarity", width: 80, render: emptyText },
        { title: "触发次数", dataIndex: "triggerCount", width: 100 },
        { title: "玩家数", dataIndex: "userCount", width: 90 },
        { title: "运行数", dataIndex: "runCount", width: 90 },
        { title: "目标数", dataIndex: "targetCount", width: 90 },
        { title: "平均目标", dataIndex: "averageTargets", width: 100, render: decimalText },
        { title: "数值总和", dataIndex: "amountSum", width: 100, render: decimalText },
        { title: "平均数值", dataIndex: "averageAmount", width: 100, render: decimalText },
        { title: "保护层", dataIndex: "stackSum", width: 90, render: decimalText },
        { title: "生成数", dataIndex: "spawnCount", width: 90 },
        { title: "转化数", dataIndex: "transformCount", width: 90 },
        { title: "消除数", dataIndex: "eliminateCount", width: 90 },
        { title: "被保护数", dataIndex: "blockedCount", width: 100 },
        { title: "延长回合", dataIndex: "extendRoundCount", width: 100 },
      ]}
    />
  );
}

function ChoiceStatsTable({ data, loading }: { data: any[]; loading: boolean }) {
  return (
    <Table
      rowKey={(row) => row.prototypeId || row.assetId || row.name || "unknown"}
      size="small"
      loading={loading}
      dataSource={data}
      scroll={{ x: 1120 }}
      columns={[
        { title: "候选道具", dataIndex: "name", width: 180, render: emptyText },
        { title: "原型编号", dataIndex: "prototypeId", width: 220, render: emptyText },
        { title: "资源编号", dataIndex: "assetId", width: 140, render: emptyText },
        { title: "类型", dataIndex: "type", width: 80, render: emptyText },
        { title: "品质", dataIndex: "rarity", width: 80, render: emptyText },
        { title: "出现次数", dataIndex: "offerCount", width: 100 },
        { title: "展示弹窗", dataIndex: "showEventCount", width: 100 },
        { title: "出现玩家", dataIndex: "offerUserCount", width: 100 },
        { title: "选择次数", dataIndex: "selectedCount", width: 100 },
        { title: "选择玩家", dataIndex: "selectedUserCount", width: 100 },
        { title: "选择率", dataIndex: "selectRate", width: 130, render: renderProgressRate },
        { title: "平均选择耗时", dataIndex: "averageSelectDurationMs", width: 130, render: durationText },
      ]}
    />
  );
}

function EventTable({ data, loading }: { data: any[]; loading: boolean }) {
  return (
    <Table
      rowKey={(row) => row.eventName || "unknown"}
      size="small"
      loading={loading}
      dataSource={data}
      columns={[
        { title: "事件", dataIndex: "eventName", render: renderEventName },
        { title: "次数", dataIndex: "count", width: 120 },
        { title: "玩家数", dataIndex: "userCount", width: 120 },
        { title: "会话数", dataIndex: "sessionCount", width: 120 },
        { title: "关卡运行数", dataIndex: "gameRunCount", width: 120 },
      ]}
    />
  );
}

function SessionTable({ data, loading }: { data: any[]; loading: boolean }) {
  return (
    <Table
      rowKey={(row) => row.gameRunId || row.sessionId}
      size="small"
      loading={loading}
      dataSource={data}
      scroll={{ x: 1300 }}
      columns={[
        { title: "关卡运行编号", dataIndex: "gameRunId", width: 220 },
        { title: "会话编号", dataIndex: "sessionId", width: 220 },
        { title: "角色编号", dataIndex: "characterId", width: 220 },
        { title: "段位", dataIndex: "grade", width: 80 },
        { title: "关卡", dataIndex: "level", width: 80 },
        { title: "事件数", dataIndex: "eventCount", width: 90 },
        { title: "最后事件", dataIndex: "lastEvent", width: 160, render: renderEventName },
        { title: "事件序列", dataIndex: "events", width: 260, render: renderEventSequence },
        { title: "首次时间", dataIndex: "firstTime", width: 180, render: timeText },
        { title: "最后时间", dataIndex: "lastTime", width: 180, render: timeText },
      ]}
    />
  );
}

function PlatformTable({ data, loading }: { data: any[]; loading: boolean }) {
  return (
    <Table
      rowKey={(row) => [row.platform, row.appVersion, row.resourceVersion].join(":")}
      size="small"
      loading={loading}
      dataSource={data}
      pagination={false}
      columns={[
        { title: "平台", dataIndex: "platform", width: 100, render: emptyText },
        { title: "应用版本", dataIndex: "appVersion", width: 110, render: emptyText },
        { title: "资源版本", dataIndex: "resourceVersion", width: 110, render: emptyText },
        { title: "次数", dataIndex: "count", width: 80 },
        { title: "玩家数", dataIndex: "userCount", width: 80 },
      ]}
    />
  );
}

async function queryAnalytics(kind: AnalyticsKind, params: Record<string, any>) {
  const res = await call("editor/AnalyticsQuery" as any, {
    timestamp: Date.now(),
    token: localStorage.getItem("token") || "",
    kind,
    ...params,
  } as any, { timeout: 60000 });
  if (!res.isSucc) {
    message.error(res.err?.message || "数据统计查询失败");
    return undefined;
  }
  return res.res.data;
}

function buildRiskyTasks(rows: any[]) {
  return rows
    .map((row) => {
      const started = Number(row.started || 0);
      const failRate = rate(row.fail, started);
      const exitRate = rate(row.exit, started);
      const riskRate = Math.max(failRate, exitRate);
      const riskCount = Number(row.fail || 0) + Number(row.exit || 0);
      return {
        key: [row.grade, row.level, row.taskIndex, row.taskId].join(":"),
        name: `段位${emptyText(row.grade)} 关卡${emptyText(row.level)} 任务${emptyText(row.taskIndex)}`,
        detail: `开始 ${integerText(started)}，成功 ${integerText(row.success)}，失败 ${integerText(row.fail)}，退出 ${integerText(row.exit)}`,
        rate: riskRate,
        rateLabel: `失败 ${percent(failRate)} / 退出 ${percent(exitRate)}`,
        countLabel: `${integerText(riskCount)} 次`,
      };
    })
    .filter((row) => row.rate > 0 || row.countLabel !== "0 次")
    .sort((a, b) => b.rate - a.rate)
    .slice(0, 8);
}

function buildPropRisks(rows: any[]) {
  return rows
    .map((row) => {
      const selected = Number(row.selected || 0);
      const used = Number(row.used || 0);
      const unused = Math.max(0, selected - used);
      const unusedRate = selected ? unused / selected : 0;
      return {
        key: row.prototypeId || row.assetId || row.name,
        name: row.name || row.prototypeId || "未命名道具",
        detail: `获得 ${integerText(row.acquired)}，选择 ${integerText(selected)}，使用 ${integerText(used)}`,
        rate: unusedRate,
        rateLabel: `未使用 ${percent(unusedRate)}`,
        countLabel: `${integerText(unused)} 次`,
      };
    })
    .filter((row) => row.rate > 0 || row.countLabel !== "0 次")
    .sort((a, b) => b.rate - a.rate)
    .slice(0, 8);
}


function buildRoundRisks(rows: any[]) {
  const maxMagic = Math.max(...rows.map((row) => Number(row.averageRoundMagic || 0)), 1);
  return rows
    .filter((row) => Number(row.roundCount || 0) > 0)
    .map((row) => {
      const averageMagic = Number(row.averageRoundMagic || 0);
      const riskRate = Math.max(0, 1 - averageMagic / maxMagic);
      return {
        key: [row.grade, row.level, row.taskIndex, row.commissionIndex, row.roundIndex].join(":"),
        name: "段位" + emptyText(row.grade) + " 关卡" + emptyText(row.level) + " 委托" + emptyText(row.commissionIndex) + " 回合" + emptyText(row.roundIndex),
        detail: "样本 " + integerText(row.roundCount) + "，盘面 " + decimalText(row.averageBoardItemCount) + "，累计 " + decimalText(row.averageTotalMagic),
        rate: riskRate,
        rateLabel: "平均产出 " + decimalText(averageMagic),
        countLabel: integerText(row.runCount) + " 局",
      };
    })
    .sort((a, b) => b.rate - a.rate)
    .slice(0, 8);
}

function buildEffectHighlights(rows: any[]) {
  const maxTrigger = Math.max(...rows.map((row) => Number(row.triggerCount || 0)), 1);
  return rows
    .filter((row) => Number(row.triggerCount || 0) > 0)
    .map((row) => {
      const triggerCount = Number(row.triggerCount || 0);
      return {
        key: [row.sourcePrototypeId, row.effectKind].join(":"),
        name: emptyText(row.sourceName || row.sourcePrototypeId) + " / " + effectText(row.effectKind),
        detail: "目标 " + integerText(row.targetCount) + "，数值 " + decimalText(row.amountSum) + "，运行 " + integerText(row.runCount),
        rate: triggerCount / maxTrigger,
        rateLabel: "高频占比 " + percent(triggerCount / maxTrigger),
        countLabel: integerText(triggerCount) + " 次",
      };
    })
    .sort((a, b) => Number(b.countLabel.replace(/D/g, "") || 0) - Number(a.countLabel.replace(/D/g, "") || 0))
    .slice(0, 8);
}

function buildChoiceRisks(rows: any[]) {
  return rows
    .filter((row) => Number(row.offerCount || 0) > 0)
    .map((row) => {
      const selectRate = rate(row.selectedCount, row.offerCount);
      return {
        key: row.prototypeId || row.assetId || row.name,
        name: row.name || row.prototypeId || "未命名候选",
        detail: "出现 " + integerText(row.offerCount) + "，选择 " + integerText(row.selectedCount) + "，玩家 " + integerText(row.offerUserCount),
        rate: 1 - selectRate,
        rateLabel: "未选 " + percent(1 - selectRate),
        countLabel: percent(selectRate),
      };
    })
    .sort((a, b) => b.rate - a.rate)
    .slice(0, 8);
}

function buildTrendRows(rows: any[]) {
  const byDay: Record<string, any> = {};
  rows.forEach((row) => {
    if (!row.day) return;
    if (!byDay[row.day]) {
      byDay[row.day] = { day: row.day, levelEnter: 0, taskStart: 0, taskSuccess: 0, taskFail: 0 };
    }
    if (row.eventName === "level_enter") byDay[row.day].levelEnter += Number(row.count || 0);
    if (row.eventName === "task_start") byDay[row.day].taskStart += Number(row.count || 0);
    if (row.eventName === "task_success") byDay[row.day].taskSuccess += Number(row.count || 0);
    if (row.eventName === "task_fail") byDay[row.day].taskFail += Number(row.count || 0);
  });
  return Object.values(byDay).sort((a, b) => String(a.day).localeCompare(String(b.day)));
}

function findWorstFunnelStep(steps: any[]) {
  return steps
    .filter((step) => step.nextStep)
    .reduce<any | undefined>((worst, step) => {
      if (!worst) return step;
      return Number(step.dropRate || 0) > Number(worst.dropRate || 0) ? step : worst;
    }, undefined);
}

function exportAnalyticsCsv(data: { overview: any; funnel: any; taskStats: any[]; propStats: any[]; roundStats: any[]; effectStats: any[]; choiceStats: any[]; retention: any; sessions: any[] }) {
  const sections: string[][] = [];
  addSection(sections, "事件统计", ["事件", "次数", "玩家数", "会话数", "关卡运行数"], (data.overview?.events || []).map((row: any) => [eventText(row.eventName), row.count, row.userCount, row.sessionCount, row.gameRunCount]));
  addSection(sections, "流程流失", ["步骤", "到达数", "进入下一步", "流失数", "流失率", "转化率", "平均耗时", "流失后行为"], (data.funnel?.steps || []).map((row: any) => [eventText(row.step), row.reached, row.next, row.dropped, percent(row.dropRate), percent(row.conversionRate), durationText(row.averageTimeToNext), dropNextEventsText(row.dropNextEvents)]));
  addSection(sections, "留存分析", ["Cohort 日期", "进入关卡玩家", "D1", "D1 留存率", "D3", "D3 留存率", "D7", "D7 留存率"], (data.retention?.rows || []).map((row: any) => [row.day, row.users, matureText(row.d1), matureRateText(row.d1Rate), matureText(row.d3), matureRateText(row.d3Rate), matureText(row.d7), matureRateText(row.d7Rate)]));
  addSection(sections, "委托统计", ["段位", "关卡", "任务序号", "任务组编号", "任务编号", "运行数", "委托数", "随机药水种类", "曝光数", "开始数", "成功数", "失败数", "退出数", "完成率", "失败率", "退出率", "平均得分", "目标均分", "平均回合"], data.taskStats.map((row: any) => [row.grade, row.level, row.taskIndex, row.taskGroupId, row.taskId, row.runCount, row.commissionCount, row.potionIdCount, row.exposed, row.started, row.success, row.fail, row.exit, percent(row.completionRate), percent(row.failRate), percent(row.exitRate), decimalText(row.averageScore), decimalText(row.averageTargetScore), decimalText(row.averageRounds)]));
  addSection(sections, "道具统计", ["道具名称", "道具原型编号", "资源编号", "类型", "品质", "玩家数", "获得数", "选择数", "使用数", "使用率", "魔力事件", "魔力总和", "平均魔力"], data.propStats.map((row: any) => [row.name, row.prototypeId, row.assetId, row.type, row.rarity, row.userCount, row.acquired, row.selected, row.used, percent(row.useRate), row.magicEvents, decimalText(row.magicSum), decimalText(row.averageMagic)]));
  addSection(sections, "回合分析", ["段位", "关卡", "任务序号", "委托序号", "回合", "样本", "玩家数", "运行数", "平均本轮魔力", "平均累计魔力", "最低魔力", "最高魔力", "盘面道具", "空格", "材料", "配方", "辅料"], data.roundStats.map((row: any) => [row.grade, row.level, row.taskIndex, row.commissionIndex, row.roundIndex, row.roundCount, row.userCount, row.runCount, decimalText(row.averageRoundMagic), decimalText(row.averageTotalMagic), decimalText(row.minRoundMagic), decimalText(row.maxRoundMagic), decimalText(row.averageBoardItemCount), decimalText(row.averageBoardEmptyCount), decimalText(row.averageMaterialCount), decimalText(row.averageRecipeCount), decimalText(row.averageConsumableCount)]));
  addSection(sections, "机制贡献", ["来源道具", "来源原型", "效果类型", "品质", "触发次数", "玩家数", "运行数", "目标数", "平均目标", "数值总和", "平均数值", "生成数", "转化数", "消除数", "被保护数", "延长回合"], data.effectStats.map((row: any) => [row.sourceName, row.sourcePrototypeId, effectText(row.effectKind), row.sourceRarity, row.triggerCount, row.userCount, row.runCount, row.targetCount, decimalText(row.averageTargets), decimalText(row.amountSum), decimalText(row.averageAmount), row.spawnCount, row.transformCount, row.eliminateCount, row.blockedCount, row.extendRoundCount]));
  addSection(sections, "三选一质量", ["候选道具", "原型编号", "资源编号", "类型", "品质", "出现次数", "展示弹窗", "出现玩家", "选择次数", "选择玩家", "选择率", "平均选择耗时"], data.choiceStats.map((row: any) => [row.name, row.prototypeId, row.assetId, row.type, row.rarity, row.offerCount, row.showEventCount, row.offerUserCount, row.selectedCount, row.selectedUserCount, percent(row.selectRate), durationText(row.averageSelectDurationMs)]));
  addSection(sections, "最近运行", ["关卡运行编号", "会话编号", "角色编号", "段位", "关卡", "事件数", "最后事件", "首次时间", "最后时间"], data.sessions.map((row: any) => [row.gameRunId, row.sessionId, row.characterId, row.grade, row.level, row.eventCount, eventText(row.lastEvent), timeText(row.firstTime), timeText(row.lastTime)]));
  const csv = sections.map(row => row.map(csvCell).join(",")).join("\n");
  const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = "游戏体验数据统计_" + dayjs().format("YYYYMMDD_HHmmss") + ".csv";
  link.click();
  URL.revokeObjectURL(url);
  message.success("已导出当前统计数据");
}

function addSection(target: string[][], title: string, headers: string[], rows: any[][]) {
  target.push([title]);
  target.push(headers);
  rows.forEach(row => target.push(row.map(value => value === undefined || value === null ? "" : String(value))));
  target.push([]);
}
function compactObject(value: Record<string, any>) {
  const result: Record<string, any> = {};
  Object.entries(value).forEach(([key, child]) => {
    if (child !== undefined && child !== null && child !== "") result[key] = child;
  });
  return result;
}

function readOptionalNumber(value: number | string | null) {
  const num = Number(value);
  return Number.isFinite(num) ? num : undefined;
}

function rate(numerator?: number, denominator?: number) {
  const num = Number(numerator || 0);
  const den = Number(denominator || 0);
  return den > 0 ? num / den : 0;
}

function percentNumber(value?: number) {
  return (Number(value) || 0) * 100;
}

function percent(value?: number) {
  return `${percentNumber(value).toFixed(1)}%`;
}

function integerText(value?: number) {
  const num = Number(value || 0);
  return Number.isFinite(num) ? num.toLocaleString("zh-CN", { maximumFractionDigits: 0 }) : "0";
}

function decimalText(value?: number) {
  const num = Number(value);
  return Number.isFinite(num) ? num.toFixed(1) : "0.0";
}

function timeText(value?: number) {
  return value ? dayjs(value).format("YYYY-MM-DD HH:mm:ss") : "-";
}

function durationText(value?: number) {
  const ms = Number(value || 0);
  if (!Number.isFinite(ms) || ms <= 0) return "-";
  if (ms < 1000) return `${Math.round(ms)}毫秒`;
  const seconds = ms / 1000;
  if (seconds < 60) return `${seconds.toFixed(1)}秒`;
  const minutes = seconds / 60;
  if (minutes < 60) return `${minutes.toFixed(1)}分钟`;
  return `${(minutes / 60).toFixed(1)}小时`;
}

function formatRange(range?: { startTime?: number; endTime?: number }) {
  if (!range?.startTime || !range?.endTime) return "";
  return `${timeText(range.startTime)} 至 ${timeText(range.endTime)}`;
}

function eventText(value?: string) {
  if (!value) return "-";
  return EVENT_LABELS[value] || "未识别事件";
}

function renderEventName(value?: string) {
  if (!value) return "-";
  return <Tag>{eventText(value)}</Tag>;
}

function effectText(value?: string) {
  if (!value) return "-";
  return EFFECT_LABELS[value] || value;
}

function renderEffectName(value?: string) {
  if (!value) return "-";
  return <Tag>{effectText(value)}</Tag>;
}

function renderProgressRate(value?: number) {
  return (
    <Space size={8} style={{ minWidth: 100 }}>
      <Progress percent={percentNumber(value)} size="small" showInfo={false} strokeColor={progressColor(Number(value || 0))} style={{ width: 72 }} />
      <Text>{percent(value)}</Text>
    </Space>
  );
}

function renderRiskRate(value?: number) {
  return <Tag color={riskColor(Number(value || 0))}>{percent(value)}</Tag>;
}

function renderDropNextEvents(value?: any[]) {
  if (!Array.isArray(value) || !value.length) return "-";
  return (
    <Space size={[4, 4]} wrap>
      {value.map((item) => (
        <Tag key={item.eventName}>{eventText(item.eventName)} {integerText(item.count)}</Tag>
      ))}
    </Space>
  );
}

function renderRetentionCell(count?: number | null, value?: number | null) {
  if (count === null || value === null || count === undefined || value === undefined) return <Tag>未成熟</Tag>;
  return <Space><Text>{integerText(count)}</Text><Tag color={riskColor(1 - Number(value || 0))}>{percent(Number(value || 0))}</Tag></Space>;
}

function renderEventSequence(value?: string[]) {
  if (!Array.isArray(value) || !value.length) return "-";
  return value.slice(0, 8).map(eventText).join(" → ") + (value.length > 8 ? " ..." : "");
}

function dropNextEventsText(value?: any[]) {
  if (!Array.isArray(value) || !value.length) return "-";
  return value.map(item => `${eventText(item.eventName)} ${integerText(item.count)}`).join(" / ");
}

function matureText(value?: number | null) {
  return value === null || value === undefined ? "未成熟" : integerText(value);
}

function matureRateText(value?: number | null) {
  return value === null || value === undefined ? "未成熟" : percent(value);
}

function riskColor(value?: number) {
  const num = Number(value || 0);
  return num >= 0.3 ? "red" : num >= 0.15 ? "gold" : "green";
}

function progressColor(value?: number) {
  const num = Number(value || 0);
  return num >= 0.6 ? "#0f766e" : num >= 0.35 ? "#d97706" : "#dc2626";
}

function emptyText(value?: any) {
  return value === undefined || value === null || value === "" ? "-" : String(value);
}

function csvCell(value: any) {
  const text = value === undefined || value === null ? "" : String(value);
  return `"${text.replace(/"/g, '""')}"`;
}
